import Indicator from "@/components/shared/Indicator";
import { TMSOrderVCInfo, TMSOrderVCType } from "@/types";
import { Col, Form, Row, Input, FormInstance, SelectProps, Select } from "antd";
import { Rule } from "antd/es/form";
import { forwardRef } from "react";

interface VCInfoFormProps {
  className?: string;
}

const VCInfoDefaultValues: TMSOrderVCInfo & {
  enabled: boolean;
} = {
  type: TMSOrderVCType.Amazon,
  carrierName: "TEST",
  bolNo: "TEST12345678",
  proNo: "TEST1111",
  arnNo: "TEST2222",
  poNo: "TEST3333",
  enabled: true,
};

const VCInfoFormFieldRules: Record<keyof TMSOrderVCInfo, Rule[]> = {
  type: [{ required: true, message: "Platform is required" }],
  carrierName: [
    // must be uppercase
    { pattern: /^[A-Z]+$/, message: "Carrier Name must be uppercase" },
  ],
  bolNo: [
    { required: true, message: "BOL No. is required" },
    // must be exactly 12 digits, uppercase alpha or numbers
    {
      validator: (_, value) => {
        if (!/^[0-9A-Za-z]{12}$/.test(value)) {
          return Promise.reject(
            new Error("must be 12 digits, uppercase alpha or numbers"),
          );
        }
        return Promise.resolve();
      },
    },
  ],
  proNo: [{ required: true, message: "PRO No. is required" }],
  arnNo: [{ required: true, message: "ARN No. is required" }],
  poNo: [{ required: true, message: "PO No. is required" }],
};

const VCInfoPlatformOptions: SelectProps["options"] = [
  { label: "Amazon", value: TMSOrderVCType.Amazon },
  { label: "BestBuy", value: TMSOrderVCType.BestBuy },
  { label: "Target", value: TMSOrderVCType.Target },
  { label: "Other", value: TMSOrderVCType.Other },
];

const VCInfoForm = forwardRef<FormInstance<TMSOrderVCInfo>, VCInfoFormProps>(
  (props, ref) => {
    const { className } = props;
    const onFormLayoutChange = () => {};
    const [form] = Form.useForm();

    return (
      <div className={className}>
        <div className="flex flex-row items-center gap-2">
          <Indicator label="VC Info" />
        </div>
        <div className="border-[1px] rounded-md p-4 bg-white border-gray-200">
          <Form
            initialValues={VCInfoDefaultValues}
            ref={ref}
            layout={"vertical"}
            form={form}
            onValuesChange={onFormLayoutChange}
          >
            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  label="Platform"
                  name="type"
                  rules={VCInfoFormFieldRules.type}
                >
                  <Select options={VCInfoPlatformOptions} />
                </Form.Item>
              </Col>

              <Col span={8}>
                <Form.Item
                  label="Carrier Name"
                  name="carrierName"
                  rules={VCInfoFormFieldRules.carrierName}
                >
                  <Input
                    onChange={(e) =>
                      form.setFieldsValue({
                        carrierName: e.target.value.toUpperCase(),
                      })
                    }
                  />
                </Form.Item>
              </Col>

              <Col span={8}>
                <Form.Item
                  label="BOL No."
                  name="bolNo"
                  rules={VCInfoFormFieldRules.bolNo}
                >
                  <Input />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={16}>
              <Col span={8}>
                <Form.Item
                  label="Pro No."
                  name="proNo"
                  rules={VCInfoFormFieldRules.proNo}
                >
                  <Input />
                </Form.Item>
              </Col>

              <Col span={8}>
                <Form.Item
                  label="ARN No."
                  name="arnNo"
                  rules={VCInfoFormFieldRules.arnNo}
                >
                  <Input />
                </Form.Item>
              </Col>

              <Col span={8}>
                <Form.Item
                  label="PO No."
                  name="poNo"
                  rules={VCInfoFormFieldRules.poNo}
                >
                  <Input />
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </div>
      </div>
    );
  },
);

export default VCInfoForm;
